import React, { useEffect, useRef, useState } from 'react';
import LinkBranded, { LinkProps } from 'src/elements/links/2023_Q4/LinkBranded';
import {
  SideBarContainer,
  SideBarContent,
  LinksList,
  ExternalLinksContainer,
  SideBarOpenStyle,
  SIDE_BAR_OPEN_TRANSITION_TIME,
} from './SideBar.styles';
import IconsCallToActionArrow from 'src/images/IconsCallToActionArrow';
import getButtonStyleByColorScheme from 'src/styles/functions/getButtonStyleByColorScheme';
import SignInButton from 'src/elements/links/2023_Q4/SignInButton';
import { useTopInfoBarController } from 'src/hooks/useTopInfoBarController';
import { IMenuLink } from '../Header.links';
import { HeaderDesignControllerContextData } from 'src/hooks/useHeaderDesignController';
import SideBarLink from './SideBarLink';

interface SideBarProps {
  isOpen: boolean;
  openStyle?: SideBarOpenStyle;
  linksCollection?: 'standard' | 'pro';
  callToAction?: 'standard' | 'pro';
  callToActionColorSchemeVariant?: LinkProps['colorSchemeVariant'];
  callToActionColorScheme?: LinkProps['colorScheme'];
  callToActionText?: string;
  backgroundTheme: HeaderDesignControllerContextData['backgroundTheme'];
  textColorTheme: HeaderDesignControllerContextData['textColorTheme'];
  // An additional Statsig Event when CTA is clicked, independent of "cta_clicked"
  callToActionStatsigEvent?: string;
  internalLinks: IMenuLink[];
}

const SideBar = ({
  isOpen,
  openStyle = 'compressed',
  callToAction = 'standard',
  callToActionColorSchemeVariant = 'secondary',
  callToActionColorScheme = 'StandardYellow',
  callToActionText,
  callToActionStatsigEvent = null,
  internalLinks,
  backgroundTheme,
  textColorTheme,
}: SideBarProps): JSX.Element => {
  const timeoutToggle = useRef<NodeJS.Timeout | null>(null);
  const [backgroundThemeHold, setBackgroundThemeHold] =
    useState<HeaderDesignControllerContextData['backgroundTheme']>(null);
  const [textColorThemeHold, settextColorThemeHold] =
    useState<HeaderDesignControllerContextData['textColorTheme']>(null);

  useEffect(() => {
    if (isOpen) {
      // Avoids flickering background changes when toggling the sidebar aggressively
      if (timeoutToggle.current) {
        clearTimeout(timeoutToggle.current);
        timeoutToggle.current = null;
      }
      setBackgroundThemeHold(backgroundTheme);
      settextColorThemeHold(textColorTheme);
    } else {
      // Keeps the SET color theme until the sidebar is fully closed
      timeoutToggle.current = setTimeout(() => {
        setBackgroundThemeHold(backgroundTheme);
        settextColorThemeHold(textColorTheme);
      }, SIDE_BAR_OPEN_TRANSITION_TIME);
    }
  }, [backgroundTheme, textColorTheme, isOpen]);

  const arrowColor = () =>
    getButtonStyleByColorScheme(callToActionColorScheme, callToActionColorSchemeVariant).textColor;

  const { isVisible } = useTopInfoBarController();

  const defaultCallToActionText = callToAction === 'pro' ? 'Get a Custom Demo' : 'Start Free Trial';
  const text = callToActionText ?? defaultCallToActionText;

  const renderCallToAction = () => (
    <LinkBranded
      text={text}
      url={callToAction === 'pro' ? '/request-a-call-form' : 'https://cohost.mn.co/?plan=free-trial'}
      standardSize='1.5'
      sizeSm='1.5rem'
      colorSchemeVariant={callToActionColorSchemeVariant}
      colorScheme={callToActionColorScheme}
      customBorderRadius={26}
      customFontWeight={700}
      iconPosition='right'
      statsigEvent={callToActionStatsigEvent}
      icon={
        <IconsCallToActionArrow
          color={arrowColor}
          width={20}
        />
      }
    />
  );

  return (
    <SideBarContainer
      isOpen={isOpen}
      openStyle={openStyle}
      hasTopInfoBar={isVisible}
      backgroundTheme={backgroundThemeHold || backgroundTheme}
      textColorTheme={textColorThemeHold || textColorTheme}
    >
      <SideBarContent openStyle={openStyle}>
        <LinksList>
          {internalLinks.map((link, index) => (
            <SideBarLink
              key={index}
              {...link}
            />
          ))}
        </LinksList>
        <ExternalLinksContainer>
          {renderCallToAction()}
          <SignInButton
            standardSize='1.5'
            sizeSm='1.5rem'
            parentBackgroundTheme='dark'
            buttonBaseColorScheme={backgroundTheme === 'light' ? 'Black' : 'White'}
          />
        </ExternalLinksContainer>
      </SideBarContent>
    </SideBarContainer>
  );
};

export default SideBar;
